import React, { useState, useEffect, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import DoneAllIcon from "@material-ui/icons/DoneAll";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TicketViewModal from "../../components/TicketViewModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll", overflowX: "auto",
    ...theme.scrollbarStyles
  },
  cellText: {
    maxWidth: 360,
    whiteSpace: "pre-wrap"
  },
  clickableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  }
}));

const STATUS_TABS = ["pending", "confirmed", "dismissed"];

const STATUS_LABEL = {
  pending: "Pendentes",
  confirmed: "Confirmadas",
  dismissed: "Dispensadas"
};

const SOURCE_LABEL = {
  no_reply_timeout: "Sem resposta",
  ai_closing_analysis: "IA no fechamento",
  manual: "Manual"
};

// Mesma logica da moderacao de FAQ (pages/FaqModeration) - IA sugere,
// fica pendente ate um usuario com permissao confirmar ou dispensar.
// Confirmar cria/move o card na coluna Pendencias do funil da fila do
// ticket; dispensar so' descarta a sugestao, sem efeito no ticket.
const PendingConfirmation = () => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewingTicketUuid, setViewingTicketUuid] = useState(null);

  const status = STATUS_TABS[tab];

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pending-candidates", { params: { status } });
      setCandidates(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  const handleChangeStatus = async (candidate, newStatus) => {
    try {
      await api.put(`/pending-candidates/${candidate.id}`, { status: newStatus });
      toast.success(
        newStatus === "confirmed" ? "Enviado para a coluna Pendências." : "Sugestão dispensada."
      );
      setCandidates(prev => prev.filter(c => c.id !== candidate.id));
    } catch (err) {
      toastError(err);
    }
  };

  // Pedido do usuario 2026-09-29: "ao clicar nos atendimentos pendentes
  // e confirmados a mesma abra o ticket correspondente" - a linha
  // inteira abre o ticket, os botoes de acao continuam funcionando por
  // cima (stopPropagation) sem disparar a abertura junto. Fix
  // 2026-09-29 (2): "poderia abrir um popup" - trocado de navegar pra
  // /tickets/:uuid (que tirava o usuario da lista) por um modal
  // (TicketViewModal), mantendo a tela de Confirmacao de Pendencias
  // aberta no fundo.
  const handleOpenTicket = candidate => {
    if (candidate.ticket?.uuid) {
      setViewingTicketUuid(candidate.ticket.uuid);
    }
  };

  const handleResolve = async candidate => {
    try {
      await api.post(`/pending-candidates/${candidate.id}/resolve`);
      toast.success("Pendência marcada como resolvida.");
      setCandidates(prev =>
        prev.map(c => (c.id === candidate.id ? { ...c, resolved: true } : c))
      );
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <TicketViewModal
        open={!!viewingTicketUuid}
        onClose={() => setViewingTicketUuid(null)}
        ticketId={viewingTicketUuid}
      />
      <MainHeader>
        <Title>Confirmação de Pendências</Title>
      </MainHeader>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary">
        {STATUS_TABS.map(s => (
          <Tab key={s} label={STATUS_LABEL[s]} />
        ))}
      </Tabs>

      <div style={{ padding: 8, fontSize: 13, color: "#666" }}>
        Candidatos são sugeridos automaticamente (cliente sem resposta há tempo, ou a IA identifica algo em
        aberto ao fechar o ticket). Confirmar cria o card na coluna "Pendências" do funil da fila do ticket.
      </div>

      <div className={classes.mainPaper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>Fila</TableCell>
              <TableCell>Motivo</TableCell>
              <TableCell>Origem</TableCell>
              {(status === "pending" || status === "confirmed") && (
                <TableCell align="center">{status === "pending" ? "Ações" : "Status"}</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={5} />
            ) : (
              candidates.map(candidate => (
                <TableRow
                  key={candidate.id}
                  className={candidate.ticket?.uuid ? classes.clickableRow : undefined}
                  onClick={() => handleOpenTicket(candidate)}
                >
                  <TableCell>{candidate.ticket?.contact?.name || "-"}</TableCell>
                  <TableCell>{candidate.queue?.name || "-"}</TableCell>
                  <TableCell className={classes.cellText}>{candidate.reason}</TableCell>
                  <TableCell>{SOURCE_LABEL[candidate.source] || candidate.source}</TableCell>
                  {status === "pending" && (
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        title="Confirmar"
                        onClick={e => {
                          e.stopPropagation();
                          handleChangeStatus(candidate, "confirmed");
                        }}
                      >
                        <CheckCircleOutlineIcon style={{ color: "#2e7d32" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Dispensar"
                        onClick={e => {
                          e.stopPropagation();
                          handleChangeStatus(candidate, "dismissed");
                        }}
                      >
                        <HighlightOffIcon style={{ color: "#c62828" }} />
                      </IconButton>
                    </TableCell>
                  )}
                  {status === "confirmed" && (
                    <TableCell align="center">
                      {candidate.resolved ? (
                        <Chip size="small" label="Resolvida" style={{ backgroundColor: "#2e7d32", color: "#fff" }} />
                      ) : (
                        <IconButton
                          size="small"
                          title="Concluir"
                          onClick={e => {
                            e.stopPropagation();
                            handleResolve(candidate);
                          }}
                        >
                          <DoneAllIcon style={{ color: "#2e7d32" }} />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
            {!loading && candidates.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Chip label={`Nenhuma sugestão ${STATUS_LABEL[status].toLowerCase()}`} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </MainContainer>
  );
};

export default PendingConfirmation;
